import { useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';

/** Renders an uploaded SVG icon for AI models (admin-trusted content). */
export function ModelIcon({
  svg,
  name,
  size = 24,
  className = '',
}: {
  svg?: string | null;
  name: string;
  size?: number;
  className?: string;
}) {
  const forceWhite = className.includes('text-white');

  if (svg?.trim()) {
    return (
      <span
        className={classNames('d-inline-flex flex-shrink-0 align-items-center justify-content-center overflow-hidden model-icon-svg', className)}
        style={{ width: size, height: size }}
        aria-hidden
      >
        <span
          className="d-inline-flex align-items-center justify-content-center w-100 h-100"
          style={forceWhite ? { filter: 'brightness(0) invert(1)' } : undefined}
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      </span>
    );
  }

  return (
    <span
      className={classNames(
        'd-inline-flex flex-shrink-0 align-items-center justify-content-center rounded-3 fw-bold',
        className || 'bg-primary text-white',
      )}
      style={{ width: size, height: size, fontSize: Math.max(10, size * 0.4) }}
    >
      {name.charAt(0).toUpperCase()}
    </span>
  );
}

interface ModelPickerProps {
  models: Array<{ id: string; publicName: string; iconSvg?: string | null }>;
  value: string;
  onChange: (id: string) => void;
  className?: string;
}

export function ModelPicker({ models, value, onChange, className = '' }: ModelPickerProps) {
  return (
    <div className={classNames('d-flex flex-column gap-2', 'overflow-auto', className)} style={{ maxHeight: '12rem' }}>
      {models.map((m) => {
        const selected = m.id === value;
        return (
          <button
            key={m.id}
            type="button"
            onClick={() => onChange(m.id)}
            className={classNames(
              'w-100 d-flex align-items-center gap-2 rounded-3 border px-3 py-2 text-start fs--1 bg-white dark__bg-card-dark',
              selected ? 'border-primary bg-primary-subtle text-primary-emphasis' : 'text-700',
            )}
          >
            <ModelIcon svg={m.iconSvg} name={m.publicName} size={28} />
            <span className="fw-medium text-truncate">{m.publicName}</span>
          </button>
        );
      })}
    </div>
  );
}

export function ModelMultiPicker({
  models,
  value,
  onChange,
  className = '',
  showToolbar = false,
  showSearch = true,
  showSelectedBadges = true,
}: {
  models: Array<{ id: string; publicName: string; iconSvg?: string | null }>;
  value: string[];
  onChange: (ids: string[]) => void;
  className?: string;
  showToolbar?: boolean;
  showSearch?: boolean;
  showSelectedBadges?: boolean;
}) {
  const [search, setSearch] = useState('');

  const filteredModels = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return models;
    return models.filter(m => m.publicName.toLowerCase().includes(q));
  }, [models, search]);

  function toggle(id: string) {
    if (value.includes(id)) {
      onChange(value.filter(x => x !== id));
      return;
    }
    onChange([...value, id]);
  }

  const allSelected = models.length > 0 && value.length === models.length;

  const selectedModels = useMemo(
    () => value.map(id => models.find(m => m.id === id)).filter(Boolean) as typeof models,
    [models, value],
  );

  function remove(id: string) {
    onChange(value.filter(x => x !== id));
  }

  return (
    <div className={className}>
      {showToolbar && models.length > 1 && (
        <div className="mb-2 d-flex align-items-center justify-content-between gap-2">
          <span className="fs--2 text-500">
            {value.length === 0
              ? 'مدلی انتخاب نشده'
              : `${value.length} از ${models.length} انتخاب شده`}
          </span>
          <div className="d-flex align-items-center gap-2 fs--2">
            <button
              type="button"
              onClick={() => onChange(models.map(m => m.id))}
              disabled={allSelected}
              className="btn btn-link p-0 fw-medium fs--2 text-primary text-decoration-none"
            >
              انتخاب همه
            </button>
            <span className="text-300" aria-hidden>
              ·
            </span>
            <button
              type="button"
              onClick={() => onChange([])}
              disabled={value.length === 0}
              className="btn btn-link p-0 fw-medium fs--2 text-500 text-decoration-none"
            >
              پاک کردن
            </button>
          </div>
        </div>
      )}

      {showSelectedBadges && selectedModels.length > 0 && (
        <div className="mb-2 d-flex flex-wrap align-items-center gap-1">
          {selectedModels.map(m => (
            <button
              key={m.id}
              type="button"
              onClick={() => remove(m.id)}
              title={`حذف ${m.publicName}`}
              className="btn d-inline-flex align-items-center gap-1 rounded-pill border border-primary px-2 py-0 fs--2 fw-medium text-1000 bg-body"
            >
              <ModelIcon svg={m.iconSvg} name={m.publicName} size={14} />
              <span className="text-truncate" style={{ maxWidth: '8rem' }}>
                {m.publicName}
              </span>
              <FontAwesomeIcon icon="times" className="fs--2 text-500" />
            </button>
          ))}
        </div>
      )}

      {showSearch && models.length > 1 && (
        <div className="position-relative mb-2">
          <FontAwesomeIcon
            icon="search"
            className="position-absolute top-50 translate-middle-y text-400"
            style={{ insetInlineStart: '0.75rem', pointerEvents: 'none' }}
          />
          <Form.Control
            type="search"
            size="sm"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="جستجوی مدل…"
            className="rounded-pill border-300"
            style={{ paddingInlineStart: '2.25rem' }}
          />
        </div>
      )}

      <div className="d-flex flex-wrap gap-2">
        {filteredModels.length === 0 ? (
          <p className="fs--1 text-500 text-center py-3 mb-0 w-100">مدلی پیدا نشد.</p>
        ) : (
          filteredModels.map(m => {
            const selected = value.includes(m.id);
            return (
              <button
                key={m.id}
                type="button"
                onClick={() => toggle(m.id)}
                aria-pressed={selected}
                className={classNames(
                  'd-flex align-items-center gap-2 rounded-3 border px-3 py-2 text-start fs--1 bg-white dark__bg-card-dark',
                  selected ? 'border-primary' : 'border-300 text-700',
                )}
                style={{ minWidth: '9.5rem', flex: '1 1 9.5rem', maxWidth: '100%' }}
              >
                <ModelIcon svg={m.iconSvg} name={m.publicName} size={24} />
                <span
                  className={classNames(
                    'fw-medium text-truncate flex-grow-1',
                    selected ? 'text-1000' : 'text-700',
                  )}
                >
                  {m.publicName}
                </span>
                <span
                  className={classNames(
                    'd-flex flex-shrink-0 align-items-center justify-content-center rounded-1 border fs--2 fw-bold',
                    selected
                      ? 'border-primary bg-primary text-white'
                      : 'border-400 bg-white dark__bg-card-dark text-transparent',
                  )}
                  style={{ width: '1rem', height: '1rem' }}
                  aria-hidden
                >
                  ✓
                </span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

export function ModelBadge({
  publicName,
  iconSvg,
}: {
  publicName: string;
  iconSvg?: string | null;
}) {
  return (
    <span className="d-inline-flex align-items-center gap-1 rounded-2 bg-200 px-2 py-1 fs--2 text-700">
      <ModelIcon svg={iconSvg} name={publicName} size={16} />
      <span>{publicName}</span>
    </span>
  );
}
